import { jsx, jsxs } from "react/jsx-runtime";
import { c } from "react/compiler-runtime";
import { styled } from "@mui/material/styles";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { BarChart } from "@mui/x-charts/BarChart";
import Box from "@mui/material/Box";
import { formatValue } from "../utils/data.js";
import Value from "../Value/Value.js";
import ComponentWrapper from "../ComponentWrapper/ComponentWrapper.js";
import TrendWithIcon from "../Trend/WithIcon.js";
const StyledBarChart = styled(BarChart, {
  shouldForwardProp: (prop) => !["showLabels"].includes(prop)
})(() => ({
  display: "flex",
  flexDirection: "column",
  position: "relative",
  ["& .MuiBarElement-root"]: {
    ".color-scheme-sand &": {
      fill: "url(#sand-bar-gradient)"
    },
    ".color-scheme-forest &": {
      fill: "url(#forest-bar-gradient)"
    },
    ".color-scheme-blaze &": {
      fill: "url(#blaze-bar-gradient)"
    },
    ".color-scheme-volt &": {
      fill: "url(#volt-bar-gradient)"
    },
    strokeWidth: 1
  },
  ["& .MuiChartsAxis-root"]: {
    display: "none",
    "& .MuiChartsAxis-line, & .MuiChartsAxis-tick, .MuiChartsAxis-label": {
      display: "none"
    }
  }
}));
const Stack = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  position: "relative"
}));
const HorizontalContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center"
}));
const Overlay = styled(HorizontalContainer)(() => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  pointerEvents: "none",
  zIndex: 10
}));
const GridOverlay = styled(Box)(({
  theme
}) => ({
  flex: 1,
  height: "100%",
  width: "100%",
  borderLeft: `1px solid ${theme.colors.border.secondary}`,
  ["&:last-child"]: {
    borderRight: `1px solid ${theme.colors.border.secondary}`
  },
  pointerEvents: "none"
}));
const BarTrendChart = (t0) => {
  const $ = c(84);
  const {
    seriesData,
    isLoading: t1,
    configuration,
    height,
    noComparison: t2
  } = t0;
  const isLoading = t1 === void 0 ? false : t1;
  const noComparison = t2 === void 0 ? false : t2;
  let t3;
  bb0: {
    if (!seriesData || !configuration || isLoading) {
      t3 = null;
      break bb0;
    }
    const {
      data,
      columns
    } = seriesData;
    let barFormats;
    let barValues;
    let labels;
    let retentionFormats;
    let retentionValues;
    let t42;
    if ($[0] !== columns || $[1] !== configuration?.series || $[2] !== data) {
      const valuesKeys = configuration?.series?.map(_temp) || [];
      let t53;
      if ($[9] !== columns) {
        t53 = (key) => columns.findIndex((col) => col.key === key);
        $[9] = columns;
        $[10] = t53;
      } else {
        t53 = $[10];
      }
      const valuesIdxs = valuesKeys.map(t53);
      let t62;
      if ($[11] !== data) {
        t62 = (idx) => data.map((row) => row[idx]);
        $[11] = data;
        $[12] = t62;
      } else {
        t62 = $[12];
      }
      barValues = valuesIdxs.map(t62).flat();
      let t72;
      if ($[13] !== columns) {
        t72 = (key_0) => columns.find((col_0) => col_0.key === key_0)?.type || "number";
        $[13] = columns;
        $[14] = t72;
      } else {
        t72 = $[14];
      }
      const barTypes = valuesKeys.map(t72);
      let t82;
      if ($[15] !== columns) {
        t82 = (key_1) => {
          const column = columns.find((col_1) => col_1.key === key_1);
          if (!column) {
            return {
              short: true,
              type: void 0
            };
          }
          return {
            ...column,
            type: column.type
          };
        };
        $[15] = columns;
        $[16] = t82;
      } else {
        t82 = $[16];
      }
      barFormats = valuesKeys.map(t82);
      const compKeys = configuration?.series?.map(_temp2) || [];
      let t92;
      if ($[17] !== columns) {
        t92 = (key_2) => columns.findIndex((col_2) => col_2.key === key_2);
        $[17] = columns;
        $[18] = t92;
      } else {
        t92 = $[18];
      }
      const compIdxs = compKeys.map(t92);
      let t102;
      if ($[19] !== data) {
        t102 = (idx_0) => data.map((row_0) => row_0[idx_0]);
        $[19] = data;
        $[20] = t102;
      } else {
        t102 = $[20];
      }
      const compValues = compIdxs.map(t102).flat();
      const retentionKeys = configuration?.series?.map(_temp3) || [];
      let t112;
      if ($[21] !== columns) {
        t112 = (key_3) => columns.findIndex((col_3) => col_3.key === key_3);
        $[21] = columns;
        $[22] = t112;
      } else {
        t112 = $[22];
      }
      const retentionIdxs = retentionKeys.map(t112);
      let t122;
      if ($[23] !== data) {
        t122 = (idx_1) => data.map((row_1) => row_1[idx_1]);
        $[23] = data;
        $[24] = t122;
      } else {
        t122 = $[24];
      }
      const retentionData = retentionIdxs.map(t122).flat();
      let t132;
      if ($[25] !== columns) {
        t132 = (key_4) => columns.find((col_4) => col_4.key === key_4)?.type || "number";
        $[25] = columns;
        $[26] = t132;
      } else {
        t132 = $[26];
      }
      const retentionTypes = retentionKeys.map(t132);
      let t142;
      if ($[27] !== columns) {
        t142 = (key_5) => {
          const column_0 = columns.find((col_5) => col_5.key === key_5);
          if (!column_0) {
            return {
              short: true
            };
          }
          return {
            ...column_0,
            type: column_0.type
          };
        };
        $[27] = columns;
        $[28] = t142;
      } else {
        t142 = $[28];
      }
      retentionFormats = retentionKeys.map(t142);
      let t152;
      if ($[29] !== configuration?.series) {
        t152 = configuration?.series?.map(_temp4) || [];
        $[29] = configuration?.series;
        $[30] = t152;
      } else {
        t152 = $[30];
      }
      labels = t152;
      const desiredTrends = configuration?.series?.map(_temp5) || [];
      retentionValues = retentionData.map((val, idx_2) => ({
        value: val,
        type: retentionTypes[idx_2] ?? "percentage"
      }));
      t42 = barValues.map((val_0, idx_3) => {
        const comp = compValues[idx_3] ?? null;
        if (comp === null || val_0 === null) {
          return null;
        }
        if (comp === 0) {
          return null;
        }
        const format = barFormats[idx_3] || {
          short: true
        };
        return {
          comparison: {
            original: val_0,
            comparison: comp,
            desiredTrend: desiredTrends[idx_3] ?? "positive"
          },
          original: {
            value: val_0,
            type: barTypes[idx_3] ?? "number"
          },
          format
        };
      });
      $[0] = columns;
      $[1] = configuration?.series;
      $[2] = data;
      $[3] = barFormats;
      $[4] = barValues;
      $[5] = labels;
      $[6] = retentionFormats;
      $[7] = retentionValues;
      $[8] = t42;
    } else {
      barFormats = $[3];
      barValues = $[4];
      labels = $[5];
      retentionFormats = $[6];
      retentionValues = $[7];
      t42 = $[8];
    }
    const comparisons = t42;
    let t52;
    if ($[31] !== barFormats || $[32] !== barValues || $[33] !== comparisons || $[34] !== labels || $[35] !== retentionFormats || $[36] !== retentionValues) {
      t52 = {
        labels,
        barValues,
        comparisons,
        retentionValues,
        barFormats,
        retentionFormats
      };
      $[31] = barFormats;
      $[32] = barValues;
      $[33] = comparisons;
      $[34] = labels;
      $[35] = retentionFormats;
      $[36] = retentionValues;
      $[37] = t52;
    } else {
      t52 = $[37];
    }
    t3 = t52;
  }
  const dataset = t3;
  if (!dataset || isLoading) {
    let t42;
    if ($[38] === /* @__PURE__ */ Symbol.for("react.memo_cache_sentinel")) {
      t42 = /* @__PURE__ */ jsx(Skeleton, { variant: "rectangular", width: "100%", height: 260 });
      $[38] = t42;
    } else {
      t42 = $[38];
    }
    return t42;
  }
  let t4;
  if ($[39] === /* @__PURE__ */ Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      position: "relative",
      width: "100%"
    };
    $[39] = t4;
  } else {
    t4 = $[39];
  }
  let t5;
  if ($[40] !== dataset.labels || $[41] !== dataset.retentionFormats || $[42] !== dataset.retentionValues) {
    let t62;
    if ($[44] !== dataset.retentionFormats || $[45] !== dataset.retentionValues) {
      t62 = (label, idx_4) => /* @__PURE__ */ jsx(Box, { sx: {
        flex: 1
      }, children: /* @__PURE__ */ jsxs(Box, { sx: {
        paddingLeft: "7.5%"
      }, children: [
        /* @__PURE__ */ jsx(Typography, { variant: "heading-xs", noWrap: true, children: label }),
        (dataset.retentionValues[idx_4]?.value || dataset.retentionValues[idx_4]?.value === 0) && /* @__PURE__ */ jsx(Value, { data: dataset.retentionValues[idx_4], format: dataset.retentionFormats[idx_4] ?? {
          short: true
        } })
      ] }) }, `label-${idx_4}`);
      $[44] = dataset.retentionFormats;
      $[45] = dataset.retentionValues;
      $[46] = t62;
    } else {
      t62 = $[46];
    }
    t5 = dataset.labels.map(t62);
    $[40] = dataset.labels;
    $[41] = dataset.retentionFormats;
    $[42] = dataset.retentionValues;
    $[43] = t5;
  } else {
    t5 = $[43];
  }
  let t6;
  if ($[47] !== t5) {
    t6 = /* @__PURE__ */ jsx(HorizontalContainer, { children: t5 });
    $[47] = t5;
    $[48] = t6;
  } else {
    t6 = $[48];
  }
  let t7;
  if ($[49] === /* @__PURE__ */ Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      horizontal: false,
      vertical: false
    };
    $[49] = t7;
  } else {
    t7 = $[49];
  }
  let t8;
  if ($[50] !== dataset.barFormats) {
    t8 = (value, context) => {
      const fmt = dataset.barFormats[context.dataIndex];
      if (!fmt?.type || value === null) {
        return "—";
      }
      return formatValue(value, {
        type: fmt.type,
        short: fmt.short ?? true
      });
    };
    $[50] = dataset.barFormats;
    $[51] = t8;
  } else {
    t8 = $[51];
  }
  let t9;
  if ($[52] !== dataset.barValues || $[53] !== t8) {
    t9 = [{
      data: dataset.barValues,
      color: "transparent",
      valueFormatter: t8
    }];
    $[52] = dataset.barValues;
    $[53] = t8;
    $[54] = t9;
  } else {
    t9 = $[54];
  }
  let t10;
  let t11;
  if ($[55] === /* @__PURE__ */ Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      left: 0,
      right: 0,
      bottom: 0
    };
    t11 = [{
      width: 0,
      position: "none"
    }];
    $[55] = t10;
    $[56] = t11;
  } else {
    t10 = $[55];
    t11 = $[56];
  }
  let t12;
  if ($[57] !== dataset.labels) {
    t12 = dataset.labels.map(_temp6);
    $[57] = dataset.labels;
    $[58] = t12;
  } else {
    t12 = $[58];
  }
  let t13;
  if ($[59] !== t12) {
    t13 = [{
      data: t12,
      height: 0,
      position: "none",
      categoryGapRatio: 0.15
    }];
    $[59] = t12;
    $[60] = t13;
  } else {
    t13 = $[60];
  }
  let t14;
  if ($[61] !== height || $[62] !== t13 || $[63] !== t9) {
    t14 = /* @__PURE__ */ jsx(StyledBarChart, { skipAnimation: true, height, borderRadius: 6, grid: t7, series: t9, margin: t10, yAxis: t11, xAxis: t13 });
    $[61] = height;
    $[62] = t13;
    $[63] = t9;
    $[64] = t14;
  } else {
    t14 = $[64];
  }
  let t15;
  if ($[65] !== dataset.barFormats || $[66] !== dataset.barValues || $[67] !== dataset.comparisons || $[68] !== isLoading || $[69] !== noComparison) {
    t15 = !noComparison && dataset.barValues.map((value_0, idx_5) => {
      const comp_0 = dataset.comparisons[idx_5];
      const format_0 = dataset.barFormats[idx_5];
      return /* @__PURE__ */ jsx(Box, { sx: {
        flex: 1
      }, children: /* @__PURE__ */ jsxs(Box, { sx: {
        paddingTop: "1rem",
        paddingX: "7.5%",
        marginBottom: "0.5rem"
      }, children: [
        /* @__PURE__ */ jsx(ComponentWrapper, { className: "value", children: (value_0 || value_0 === 0) && /* @__PURE__ */ jsx(Value, { data: {
          value: value_0,
          type: format_0?.type ?? "integer"
        }, format: format_0 ?? {
          short: true,
          precision: 1
        }, isLoading, float: "left" }) }),
        /* @__PURE__ */ jsx(ComponentWrapper, { className: "value-with-trend-indicator", children: /* @__PURE__ */ jsx(TrendWithIcon, { data: comp_0?.comparison || {
          original: 0,
          comparison: 0,
          desiredTrend: "positive"
        }, isLoading }) })
      ] }) }, `label-${idx_5}`);
    });
    $[65] = dataset.barFormats;
    $[66] = dataset.barValues;
    $[67] = dataset.comparisons;
    $[68] = isLoading;
    $[69] = noComparison;
    $[70] = t15;
  } else {
    t15 = $[70];
  }
  let t16;
  if ($[71] !== t15) {
    t16 = /* @__PURE__ */ jsx(HorizontalContainer, { children: t15 });
    $[71] = t15;
    $[72] = t16;
  } else {
    t16 = $[72];
  }
  let t17;
  if ($[73] !== t14 || $[74] !== t16 || $[75] !== t6) {
    t17 = /* @__PURE__ */ jsxs(Stack, { children: [
      t6,
      t14,
      t16
    ] });
    $[73] = t14;
    $[74] = t16;
    $[75] = t6;
    $[76] = t17;
  } else {
    t17 = $[76];
  }
  let t18;
  if ($[77] !== dataset.labels) {
    t18 = dataset.labels.map(_temp7);
    $[77] = dataset.labels;
    $[78] = t18;
  } else {
    t18 = $[78];
  }
  let t19;
  if ($[79] !== t18) {
    t19 = /* @__PURE__ */ jsx(Overlay, { children: t18 });
    $[79] = t18;
    $[80] = t19;
  } else {
    t19 = $[80];
  }
  let t20;
  if ($[81] !== t17 || $[82] !== t19) {
    t20 = /* @__PURE__ */ jsxs("div", { style: t4, children: [
      t17,
      t19
    ] });
    $[81] = t17;
    $[82] = t19;
    $[83] = t20;
  } else {
    t20 = $[83];
  }
  return t20;
};
function _temp(s) {
  return s.value.key;
}
function _temp2(s_0) {
  return s_0.comp?.key;
}
function _temp3(s_1) {
  return s_1.retention?.key;
}
function _temp4(s_2) {
  return s_2.label;
}
function _temp5(s_3) {
  return s_3.desired_trend;
}
function _temp6(label_0) {
  return label_0;
}
function _temp7(label_1) {
  return /* @__PURE__ */ jsx(GridOverlay, {}, `border-${label_1}`);
}
export {
  BarTrendChart as default
};
